import UIError from '@/errors/UIError';
import { QueryClient } from '@tanstack/react-query';

import { LoaderFunctionArgs } from 'react-router';
import { ProcessObjectFile } from '../process-objects/list/types';
import apiClient from '@/api';
import { DeploymentType, FieldReport } from './field-report/types';
import { FieldReportFormSchema } from './field-report/fieldReportSchema';
import { PROCESS_OBJECTS_ROOT_API_URL } from '../process-objects/list/constants';
import {
	MeasurementGroupDetails,
	MeasurementGroupStatusType,
	MeasurementGroupStatusUpdateRequest,
	MeasurementGroupApplyRevisionRequest,
	CalculationType,
	Priority,
	Series,
	CreateMeasurementGroupRequest,
	UpdateCapacityRequest
} from './types';
import { buildQueryParams } from '@/utils/build-query-params';
import { TIME_CONSTANTS_IN_MS } from '@/constants';
import { format } from 'date-fns';

export const MEASUREMENT_GROUPS_BASE_API_URL = `/api/v1/measurement-groups`;
export const MEASUREMENT_GROUPS_STATUSES_BASE_API_URL = '/api/v1/measurement-groups-statuses';
export const DEPLOYMENT_TYPES_BASE_API_URL = `/api/v1/deployment-types`;
export const TASKS_BASE_API_URL = `/api/v1/tasks`;

export function queryMeasurementGroupStatuses() {
	return {
		queryKey: ['measurement-groups-statuses'],
		queryFn: () => {
			return apiClient.get<MeasurementGroupStatusType[]>(`${MEASUREMENT_GROUPS_STATUSES_BASE_API_URL}`);
		}
	};
}

type RealTimeGenerationQueryArgs = {
	measurementGroupId: number;
	from?: string | undefined | null;
	to?: string | undefined | null;
};

export function queryRealTimeGenerationData({ measurementGroupId, ...rest }: RealTimeGenerationQueryArgs) {
	const queryParams = buildQueryParams(rest);
	return {
		queryKey: ['measurement-groups', measurementGroupId, 'real-time-measurements', queryParams],
		queryFn: () =>
			apiClient.get<Series>(
				`${MEASUREMENT_GROUPS_BASE_API_URL}/${measurementGroupId}/real-time-measurements?${queryParams}`
			)
	};
}

type LineMeasurementsQueryArgs = {
	measurementGroupId: number;
	from?: string | undefined | null;
	to?: string | undefined | null;
	priority?: Priority | undefined | null;
	calculationType: CalculationType;
};

export function queryLineMeasurementsData({ measurementGroupId, ...rest }: LineMeasurementsQueryArgs) {
	const queryParams = buildQueryParams(rest);
	return {
		queryKey: ['measurement-groups', measurementGroupId, 'line-measurements', queryParams],
		queryFn: () =>
			apiClient.get<Series[]>(
				`${MEASUREMENT_GROUPS_BASE_API_URL}/${measurementGroupId}/line-measurements?${queryParams}`
			)
	};
}

export async function queryGeometry({
	processObjectId,
	fileId
}: {
	processObjectId: number;
	fileId: number;
}): Promise<string> {
	const response = await apiClient.get<string>(
		`${PROCESS_OBJECTS_ROOT_API_URL}/${processObjectId}/files/${fileId}/graph`,
		{
			responseType: 'text'
		}
	);
	return response.data;
}

export function fetchProcessObjectFile({
	processObjectId,
	fileId
}: {
	processObjectId: number;
	fileId: number;
}) {
	return apiClient.get(
		`${PROCESS_OBJECTS_ROOT_API_URL}/${processObjectId}/files/${fileId}`,
		{ responseType: 'blob' }
	);
}

export function queryMeasurementGroup(measurementGroupId: number) {
	return {
		queryKey: ['measurement-groups', measurementGroupId],
		queryFn: async () => {
			return await apiClient.get<MeasurementGroupDetails>(
				`${MEASUREMENT_GROUPS_BASE_API_URL}/${measurementGroupId}`
			);
		}
	};
}

function queryMeasurementGroupFiles(measurementGroupId: number) {
	return {
		queryKey: ['measurement-groups', measurementGroupId, 'files'],
		queryFn: async () => {
			return await apiClient.get<ProcessObjectFile[]>(
				`${MEASUREMENT_GROUPS_BASE_API_URL}/${measurementGroupId}/files`
			);
		}
	};
}

function queryFieldReports(measurementGroupId: number) {
	return {
		queryKey: ['measurement-groups', measurementGroupId, 'field-reports'],
		queryFn: () =>
			apiClient.get<FieldReport[]>(`${MEASUREMENT_GROUPS_BASE_API_URL}/${measurementGroupId}/field-reports`)
	};
}

export function queryDeploymentTypes() {
	return {
		queryKey: ['deployment-types'],
		queryFn: () => {
			return apiClient.get<DeploymentType[]>(DEPLOYMENT_TYPES_BASE_API_URL);
		}
	};
}

export function editFieldReport(measurementGroupId: number, fieldReportId: number, fieldReport: FieldReportFormSchema) {
	return apiClient.put<FieldReport>(
		`${MEASUREMENT_GROUPS_BASE_API_URL}/${measurementGroupId}/field-reports/${fieldReportId}`,
		fieldReport
	);
}

export function createFieldReport(measurementGroupId: number, fieldReport: FieldReportFormSchema) {
	return apiClient.post<FieldReport>(
		`${MEASUREMENT_GROUPS_BASE_API_URL}/${measurementGroupId}/field-reports`,
		fieldReport
	);
}

export function updateMeasurementGroupStatus(measurementGroupId: number, payload: MeasurementGroupStatusUpdateRequest) {
	return apiClient.patch<MeasurementGroupDetails>(
		`${MEASUREMENT_GROUPS_BASE_API_URL}/${measurementGroupId}`,
		payload
	);
}

export function saveRevisionFile(measurementGroupId: number, payload: MeasurementGroupApplyRevisionRequest) {
	const requestBody = new FormData();
	const metadata = {
		measurementGroupId: measurementGroupId,
		startTime: format(Date.now(), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
		endTime: format(Date.now(), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
		intercept: false,
		onlyHistoricalData: false,
		processObjects: []
	};
	const type = 'REVISION_DATA';
	const referenceDataFile = new File([], '');

	requestBody.append('metadata', JSON.stringify(metadata));
	requestBody.append('type', type);
	requestBody.append('referenceDataFile', referenceDataFile);
	requestBody.append('revisionDataFile', payload.revisionDataFile);

	return apiClient.post<MeasurementGroupDetails>(`${TASKS_BASE_API_URL}`, requestBody);
}

export function saveMeasurementGroup(payload: CreateMeasurementGroupRequest) {
	return apiClient.post<MeasurementGroupDetails>(`${MEASUREMENT_GROUPS_BASE_API_URL}`, payload);
}

export function loader(queryClient: QueryClient) {
	return async ({ params }: LoaderFunctionArgs) => {
		const mgID = parseInt(params.measurementGroupId ?? '');

		if (isNaN(mgID)) {
			throw new UIError('Internal Error: No Measurement Group Id provided');
		}

		const measurementGroup = await queryClient.fetchQuery({
			...queryMeasurementGroup(mgID),
			staleTime: TIME_CONSTANTS_IN_MS.FIVE_MIN
		});

		const fileList = await queryClient.fetchQuery({
			...queryMeasurementGroupFiles(mgID),
			staleTime: TIME_CONSTANTS_IN_MS.FIVE_MIN
		});

		const fieldReports = queryClient.fetchQuery({
			...queryFieldReports(mgID),
			staleTime: TIME_CONSTANTS_IN_MS.FIVE_MIN
		});

		const deploymentTypes = queryClient.ensureQueryData(queryDeploymentTypes());

		const measurementGroupStatuses = queryClient.ensureQueryData(queryMeasurementGroupStatuses());

		return {
			measurementGroup,
			fileList,
			fieldReports,
			deploymentTypes,
			measurementGroupStatuses
		};
	};
}

export function updateMinMaxCapacity(id: number, payload: UpdateCapacityRequest) {
	return apiClient.put<MeasurementGroupDetails>(`${MEASUREMENT_GROUPS_BASE_API_URL}/${id}/graph-capacity`, payload);
}
